import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule, DatePipe, JsonPipe } from '@angular/common';
import { Attribute, ChangeDetectionStrategy, Component, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenericRepository } from '@foundation/table/state';
import { BehaviorType, RepositoryTableComponent } from '@foundation/table/ui';
import { TranslateDirective } from '@foundation/translations/services';
import { Filter } from '@foundation/network/store';
import { finalize, of, switchMap } from 'rxjs';

export interface AdminTask {
	id: string;
	title?: string;
	description?: string;
	methodName?: string;
	kind?: string;
	priority?: number;
	started?: boolean;
	completed?: boolean;
	ended?: boolean;
	failed?: boolean;
	progress?: number;
	startedAt?: string;
	endedAt?: string;
	lastHeartbeatAt?: string;
	processorId?: string;
	processorKind?: string;
	artifacts?: Record<string, unknown> | null;
	timeCreated?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminTasksRepository extends GenericRepository<AdminTask> {
	constructor() {
		super('task');
	}

	getTasksPage$(page: number, pageSize: number, filters: Filter[], orderingBy: string | undefined, forceRequest: boolean) {
		return this.store.getObjects$(page, pageSize, filters, orderingBy, forceRequest, false, true);
	}

	launchProcessing$() {
		return this._requestService.getBasic$('/api/tasks/processing/launch-from-server');
	}

	retryFailed$() {
		return this._requestService.getBasic$('/api/tasks/processing/retry-failed');
	}

	recreateTask$(taskId: string) {
		return this._requestService.getBasic$<{ taskId: string }>(`/api/tasks/${taskId}/recreate`);
	}

	getEnlistedTaskMethods$() {
		return this._requestService.getBasic$<{ methodNames: string[] }>('/api/tasks/enlisted-methods');
	}
}

@Component({
	selector: 'lib-admin-tasks-page',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, CdkMenuModule, TranslateDirective, DatePipe, JsonPipe],
	templateUrl: './admin-tasks-page.component.html',
	styleUrl: './admin-tasks-page.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTasksPageComponent extends RepositoryTableComponent<AdminTask, AdminTasksRepository> implements OnDestroy {
	private _taskHeartbeatStaleAfterMilliseconds = 45_000;
	private _autoRefreshIntervalId: ReturnType<typeof setInterval> | null = null;

	private _i18n_taskProcessingLaunched = this._translationService.prep('Task processing launched.');
	private _i18n_failedTasksRetryLaunched = this._translationService.prep('Failed tasks retry launched.');
	private _i18n_taskIdCopied = this._translationService.prep('Task id copied.');
	private _i18n_recreateTaskConfirmation = this._translationService.prep('Are you sure you want to recreate this task?');
	private _i18n_recreateTaskTitle = this._translationService.prep('Recreate task');
	private _i18n_recreate = this._translationService.prep('Recreate');
	private _i18n_taskRecreated = this._translationService.prep('Task recreated.');
	private _i18n_deleteTaskConfirmation = this._translationService.prep('Are you sure you want to delete this task?');
	private _i18n_deleteTaskTitle = this._translationService.prep('Delete task');
	private _i18n_delete = this._translationService.prep('Delete');
	private _i18n_taskDeleted = this._translationService.prep('Task deleted.');

	isLaunchingProcessing = signal(false);
	isRetryingFailed = signal(false);
	isAutoRefreshEnabled = signal(false);
	enlistedTaskMethods = signal<string[]>([]);
	activeTaskActionId = signal<string | null>(null);
	activeTaskActionKind = signal<'recreate' | 'delete' | null>(null);

	constructor(
		private _repository: AdminTasksRepository,
		@Attribute('click-behavior') clickBehavior: BehaviorType
	) {
		super(
			_repository,
			{
				pageSize: 50,
				orderingBy: { fieldName: 'time_created', direction: 'desc' },
				alwaysOnFilters: [],
				requestFn: (page, pageSize, filters, orderingBy, forceRequest) => {
					return _repository.getTasksPage$(page, pageSize, filters, orderingBy, forceRequest);
				},
			},
			clickBehavior
		);

		this.loadEnlistedTaskMethods();
	}

	override ngOnDestroy() {
		super.ngOnDestroy();
		if (this._autoRefreshIntervalId !== null) {
			clearInterval(this._autoRefreshIntervalId);
			this._autoRefreshIntervalId = null;
		}
	}

	refresh() {
		this.paginator.refresh().subscribe();
	}

	loadEnlistedTaskMethods() {
		this._repository
			.getEnlistedTaskMethods$()
			.pipe(takeUntilDestroyed())
			.subscribe((response) => {
				const methodNames = response.result?.methodNames ?? [];
				this.enlistedTaskMethods.set(methodNames);
			});
	}

	launchProcessing() {
		if (this.isLaunchingProcessing()) return;
		this.isLaunchingProcessing.set(true);
		this._repository
			.launchProcessing$()
			.pipe(
				finalize(() => this.isLaunchingProcessing.set(false)),
				switchMap(() => {
					this._notificationService.snackSuccess(this._i18n_taskProcessingLaunched());
					return this.paginator.refresh();
				})
			)
			.subscribe();
	}

	retryFailedTasks() {
		if (this.isRetryingFailed()) return;
		this.isRetryingFailed.set(true);
		this._repository
			.retryFailed$()
			.pipe(
				finalize(() => this.isRetryingFailed.set(false)),
				switchMap(() => {
					this._notificationService.snackSuccess(this._i18n_failedTasksRetryLaunched());
					return this.paginator.refresh();
				})
			)
			.subscribe();
	}

	getStatusTone(task: AdminTask): string {
		if (task.failed) return 'badge-error';
		if (task.completed) return 'badge-success';
		if (this.isTaskStale(task)) return 'badge-error';
		if (this.isTaskEffectivelyRunning(task)) return 'badge-warning';
		return 'badge-ghost';
	}

	getStatusLabel(task: AdminTask): string {
		if (task.failed) return 'failed';
		if (task.completed) return 'completed';
		if (this.isTaskStale(task)) return 'stale';
		if (this.isTaskEffectivelyRunning(task)) return 'running';
		if (task.ended) return 'ended';
		return 'queued';
	}

	getProgressValue(task: AdminTask): number {
		const value = typeof task.progress === 'number' ? task.progress : 0;
		return Math.max(0, Math.min(100, value));
	}

	getHeartbeatLabel(task: AdminTask): string {
		if (!task.lastHeartbeatAt) return 'No heartbeat yet';

		const heartbeatTimestamp = Date.parse(task.lastHeartbeatAt);
		if (Number.isNaN(heartbeatTimestamp)) return 'Heartbeat unavailable';

		const elapsedSeconds = Math.max(0, Math.floor((Date.now() - heartbeatTimestamp) / 1000));
		if (elapsedSeconds < 60) return `Last heartbeat ${elapsedSeconds}s ago`;

		const elapsedMinutes = Math.floor(elapsedSeconds / 60);
		if (elapsedMinutes < 60) return `Last heartbeat ${elapsedMinutes}m ago`;

		const elapsedHours = Math.floor(elapsedMinutes / 60);
		if (elapsedHours < 24) return `Last heartbeat ${elapsedHours}h ago`;

		const elapsedDays = Math.floor(elapsedHours / 24);
		return `Last heartbeat ${elapsedDays}d ago`;
	}

	hasArtifacts(task: AdminTask): boolean {
		return !!task.artifacts && Object.keys(task.artifacts).length > 0;
	}

	toggleExpandedTask(task: AdminTask) {
		this.openedItems.toggle(task);
	}

	copyTaskId(task: AdminTask) {
		navigator.clipboard.writeText(task.id).then(() => {
			this._notificationService.snackSuccess(this._i18n_taskIdCopied());
		});
	}

	canRunTaskActions(task: AdminTask): boolean {
		return !this.isTaskEffectivelyRunning(task);
	}

	isTaskEffectivelyRunning(task: AdminTask): boolean {
		if (!task.started || task.ended) return false;
		if (!task.lastHeartbeatAt) return false;

		const heartbeatTimestamp = Date.parse(task.lastHeartbeatAt);
		if (Number.isNaN(heartbeatTimestamp)) return false;

		return Date.now() - heartbeatTimestamp <= this._taskHeartbeatStaleAfterMilliseconds;
	}

	isTaskStale(task: AdminTask): boolean {
		return !!task.started && !task.ended && !this.isTaskEffectivelyRunning(task);
	}

	isTaskActionPending(task: AdminTask, actionKind: 'recreate' | 'delete'): boolean {
		return this.activeTaskActionId() === task.id && this.activeTaskActionKind() === actionKind;
	}

	toggleAutoRefresh() {
		const shouldEnable = !this.isAutoRefreshEnabled();
		this.isAutoRefreshEnabled.set(shouldEnable);

		if (this._autoRefreshIntervalId !== null) {
			clearInterval(this._autoRefreshIntervalId);
			this._autoRefreshIntervalId = null;
		}

		if (!shouldEnable) return;

		this.refresh();
		this._autoRefreshIntervalId = setInterval(() => {
			this.refresh();
		}, 1000);
	}

	recreateTask(task: AdminTask) {
		if (!this.canRunTaskActions(task) || this.activeTaskActionId()) return;

		this._notificationService
			.confirm(this._i18n_recreateTaskConfirmation(), this._i18n_recreateTaskTitle(), {
				confirmButtonText: this._i18n_recreate(),
			})
			.closed.subscribe((confirmed) => {
				if (!confirmed) return;

				this.activeTaskActionId.set(task.id);
				this.activeTaskActionKind.set('recreate');

				this._repository
					.recreateTask$(task.id)
					.pipe(
						finalize(() => {
							this.activeTaskActionId.set(null);
							this.activeTaskActionKind.set(null);
						}),
						switchMap((response) => {
							if (response.error) return of(response);
							this._notificationService.snackSuccess(this._i18n_taskRecreated());
							return this.paginator.refresh();
						})
					)
					.subscribe();
			});
	}

	deleteTask(task: AdminTask) {
		if (!this.canRunTaskActions(task) || this.activeTaskActionId()) return;

		this._notificationService
			.confirm(this._i18n_deleteTaskConfirmation(), this._i18n_deleteTaskTitle(), {
				confirmButtonText: this._i18n_delete(),
			})
			.closed.subscribe((confirmed) => {
				if (!confirmed) return;

				this.activeTaskActionId.set(task.id);
				this.activeTaskActionKind.set('delete');

				this._repository.store
					.deleteObject$(task.id)
					.pipe(
						finalize(() => {
							this.activeTaskActionId.set(null);
							this.activeTaskActionKind.set(null);
						}),
						switchMap((response) => {
							if (response.error) return of(response);
							this._notificationService.snackSuccess(this._i18n_taskDeleted());
							return this.paginator.refresh();
						})
					)
					.subscribe();
			});
	}

	protected readonly of = of;
}
